import { normalizeBooking } from './booking';
import { buildClientNextStepRecommendations, normalizeClientProfile, suggestNextServiceForClient, suggestPackageForClient } from './clientCRM';
import { normalizeCustomerMembership, normalizeServicePackage } from './servicePackages';
import { getServicePricingSummary, isServiceEligibleForPackage, normalizeService } from './services';
import { buildStudentProgressSummary } from './progressTracking';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimestampValue(value) {
  return toDateValue(value)?.getTime?.() || 0;
}

function formatCurrencyAmount(amount = 0, currency = 'USD') {
  return `${Number(amount || 0).toLocaleString()} ${normalizeString(currency || 'USD') || 'USD'}`;
}

function safeNormalizeService(service = {}) {
  if (typeof normalizeService === 'function') {
    return normalizeService(service) || {};
  }
  return service || {};
}

function buildServiceAudience(service = {}) {
  const normalized = safeNormalizeService(service);
  return [
    normalized.discipline ? `${normalized.discipline} clients who want a structured next step.` : '',
    normalized.serviceType ? `Athletes looking for ${normalized.serviceType.replace(/_/g, ' ')} support.` : '',
    normalized.maxParticipants > 1 ? `Small groups of up to ${normalized.maxParticipants}.` : 'Clients who want focused one-on-one coaching.',
  ].filter(Boolean);
}

function buildServiceLearningOutcomes(service = {}) {
  const normalized = safeNormalizeService(service);
  return [
    normalized.shortDescription || normalized.description || '',
    normalized.locationLabel ? `Session setting: ${normalized.locationLabel}.` : '',
    normalized.durationMinutes ? `A clear ${normalized.durationMinutes}-minute structure with practical coaching takeaways.` : '',
  ].filter(Boolean);
}

function packageMatchesService(service = {}, servicePackage = {}) {
  const normalizedService = safeNormalizeService(service);
  const normalizedPackage = normalizeServicePackage(servicePackage);
  if (normalizedPackage.active === false) return false;
  if (!normalizedService.packageEligible) return false;
  if ((normalizedService.applicablePackageIds || []).length > 0) {
    return normalizedService.applicablePackageIds.includes(normalizedPackage.servicePackageId);
  }
  const disciplineMatch = !normalizedPackage.applicableDisciplines?.length || normalizedPackage.applicableDisciplines.includes(normalizedService.discipline);
  const serviceTypeMatch = !normalizedPackage.applicableServiceTypes?.length || normalizedPackage.applicableServiceTypes.includes(normalizedService.serviceType);
  return disciplineMatch && serviceTypeMatch;
}

export function getRecommendedPackageForService(
  service = {},
  {
    servicePackages = [],
    memberships = [],
  } = {}
) {
  const normalizedService = safeNormalizeService(service);
  const activeMemberships = (memberships || []).map((membership) => normalizeCustomerMembership(membership));
  const activeMatch = activeMemberships.find((membership) => isServiceEligibleForPackage(normalizedService, membership));
  if (activeMatch) {
    return {
      source: 'active_membership',
      packageId: activeMatch.packageId,
      customerMembershipId: activeMatch.customerMembershipId,
      name: 'Your active package',
      summary: 'One of your active packages may already cover this service.',
    };
  }

  const eligiblePackages = (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .filter((servicePackage) => packageMatchesService(normalizedService, servicePackage))
    .sort((left, right) => {
      if (left.includedSessionCount !== right.includedSessionCount) {
        return left.includedSessionCount - right.includedSessionCount;
      }
      return left.price - right.price;
    });

  const recommendedPackage = eligiblePackages[0] || null;
  if (!recommendedPackage) return null;

  return {
    source: 'package_catalog',
    ...recommendedPackage,
    summary: `${recommendedPackage.name} is a practical fit for ${normalizedService.name}.`,
  };
}

export function getMostPopularPackageForService(
  service = {},
  {
    servicePackages = [],
    packageUsages = [],
  } = {}
) {
  const eligiblePackages = (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .filter((servicePackage) => packageMatchesService(service, servicePackage));

  const usageCounts = eligiblePackages.map((servicePackage) => {
    const usageCount = (packageUsages || []).filter((usage) => usage.packageId === servicePackage.servicePackageId).length;
    return {
      ...servicePackage,
      usageCount,
    };
  }).sort((left, right) => right.usageCount - left.usageCount || left.price - right.price);

  return usageCounts[0] || null;
}

export function getPackageSavingsSummary(
  service = {},
  servicePackage = {}
) {
  const normalizedService = safeNormalizeService(service);
  const normalizedPackage = normalizeServicePackage(servicePackage);
  const singleLessonPrice = Number(normalizedService.price || 0);
  const includedSessions = Number(normalizedPackage.includedSessionCount || 0);
  const bundleValue = singleLessonPrice * includedSessions;
  const savingsAmount = Math.max(0, bundleValue - Number(normalizedPackage.price || 0));
  const savingsPercent = bundleValue > 0 ? Math.round((savingsAmount / bundleValue) * 100) : 0;

  return {
    singleLessonPrice,
    includedSessions,
    bundleValue,
    savingsAmount,
    savingsPercent,
    summary: savingsAmount > 0
      ? `Save ${formatCurrencyAmount(savingsAmount, normalizedPackage.currency)} versus booking single sessions.`
      : 'A package can make repeat coaching easier to plan.',
  };
}

export function getPackageComparisonData(
  service = {},
  {
    servicePackages = [],
    memberships = [],
    packageUsages = [],
    clientProfile = {},
  } = {}
) {
  const normalizedService = safeNormalizeService(service);
  const recommendedPackage = getRecommendedPackageForService(normalizedService, {
    servicePackages,
    memberships,
  });
  const popularPackage = getMostPopularPackageForService(normalizedService, {
    servicePackages,
    packageUsages,
  });
  const clientRecommendation = recommendPackageForClient(clientProfile, {
    memberships,
    servicePackages,
    bookings: [],
  });

  return (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .filter((servicePackage) => packageMatchesService(normalizedService, servicePackage))
    .map((servicePackage) => {
      const savings = getPackageSavingsSummary(normalizedService, servicePackage);
      return {
        ...servicePackage,
        savings,
        isRecommended: (recommendedPackage?.servicePackageId || recommendedPackage?.packageId) === servicePackage.servicePackageId,
        isMostPopular: popularPackage?.servicePackageId === servicePackage.servicePackageId,
        isBestValue: savings.savingsAmount > 0 && savings.savingsPercent >= 15,
        isRecommendedForClient: (clientRecommendation?.servicePackageId || clientRecommendation?.packageId) === servicePackage.servicePackageId,
      };
    })
    .sort((left, right) => {
      if (left.isRecommended !== right.isRecommended) return left.isRecommended ? -1 : 1;
      if (left.isMostPopular !== right.isMostPopular) return left.isMostPopular ? -1 : 1;
      return right.savings.savingsAmount - left.savings.savingsAmount;
    });
}

export function buildPackageSelectionView(
  service = {},
  {
    servicePackages = [],
    memberships = [],
    packageUsages = [],
    clientProfile = {},
  } = {}
) {
  const packages = getPackageComparisonData(service, {
    servicePackages,
    memberships,
    packageUsages,
    clientProfile,
  });
  const fallbackPackages = packages.length > 0
    ? packages
    : (servicePackages || [])
      .map((servicePackage) => normalizeServicePackage(servicePackage))
      .filter((servicePackage) => servicePackage.active !== false)
      .map((servicePackage) => ({
        ...servicePackage,
        savings: getPackageSavingsSummary(service, servicePackage),
        isRecommended: false,
        isMostPopular: false,
        isBestValue: false,
        isRecommendedForClient: false,
      }));

  return {
    packages: fallbackPackages,
    recommendedPackage: fallbackPackages.find((servicePackage) => servicePackage.isRecommended || servicePackage.isRecommendedForClient) || fallbackPackages[0] || null,
    mostPopularPackage: fallbackPackages.find((servicePackage) => servicePackage.isMostPopular) || null,
    bestValuePackage: fallbackPackages.find((servicePackage) => servicePackage.isBestValue) || fallbackPackages[0] || null,
  };
}

export function recommendPackageForClient(
  clientProfile = {},
  {
    memberships = [],
    servicePackages = [],
    bookings = [],
  } = {}
) {
  return suggestPackageForClient(clientProfile, {
    memberships,
    servicePackages,
    bookings,
  });
}

export function getRecommendedPackageForClient(clientProfile = {}, options = {}) {
  return recommendPackageForClient(clientProfile, options);
}

export function recommendPackageAfterLesson(
  lesson = {},
  {
    clientProfile = {},
    memberships = [],
    servicePackages = [],
    bookings = [],
  } = {}
) {
  const recommendation = recommendPackageForClient(clientProfile, {
    memberships,
    servicePackages,
    bookings,
  });
  if (recommendation) return recommendation;

  const lessonDiscipline = normalizeString(lesson.discipline || lesson.sport || '');
  return (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .find((servicePackage) =>
      servicePackage.active !== false &&
      (!servicePackage.applicableDisciplines.length || servicePackage.applicableDisciplines.includes(lessonDiscipline))
    ) || null;
}

export function recommendPackageAfterVideo(
  video = {},
  {
    clientProfile = {},
    memberships = [],
    servicePackages = [],
    bookings = [],
  } = {}
) {
  const recommendation = recommendPackageForClient(clientProfile, {
    memberships,
    servicePackages,
    bookings,
  });
  if (recommendation) return recommendation;

  const videoDiscipline = normalizeString(video.sport || '');
  return (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .find((servicePackage) =>
      servicePackage.active !== false &&
      (!servicePackage.applicableDisciplines.length || servicePackage.applicableDisciplines.includes(videoDiscipline))
    ) || null;
}

export function recommendPackageBasedOnGoals(
  clientProfile = {},
  {
    memberships = [],
    servicePackages = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const activeMemberships = (memberships || []).map((membership) => normalizeCustomerMembership(membership));
  if (activeMemberships.some((membership) => membership.userId === normalizedProfile.userId && membership.status === 'active')) {
    return null;
  }
  return (servicePackages || [])
    .map((servicePackage) => normalizeServicePackage(servicePackage))
    .find((servicePackage) => {
      if (servicePackage.active === false) return false;
      if (!normalizedProfile.primaryDiscipline) return true;
      return !servicePackage.applicableDisciplines.length || servicePackage.applicableDisciplines.includes(normalizedProfile.primaryDiscipline);
    }) || null;
}

export function buildInstructorPublicProfile(profile = {}, { services = [] } = {}) {
  const yearsExperience = Number(profile.yearsExperience || 0);
  const disciplines = profile.disciplines || [];
  const specialties = profile.specialties || [];
  return {
    ...profile,
    serviceCount: (services || []).length,
    experienceIndicator: yearsExperience > 0 ? `${yearsExperience}+ years experience` : `${(services || []).length} public service${(services || []).length === 1 ? '' : 's'}`,
    lessonCountIndicator: `${(services || []).length} active public offering${(services || []).length === 1 ? '' : 's'}`,
    specialtiesDisplay: specialties.length > 0 ? specialties : disciplines,
    whoItsFor: [
      disciplines[0] ? `${disciplines[0]} learners looking for clear coaching progression.` : '',
      specialties[0] ? `Clients focused on ${specialties[0]}.` : '',
      profile.shortBio || '',
    ].filter(Boolean),
    whatYoullLearn: [
      specialties[0] ? `Targeted coaching around ${specialties[0]}.` : '',
      specialties[1] ? `Follow-up development in ${specialties[1]}.` : '',
      disciplines[0] ? `Practical next steps in ${disciplines[0]}.` : '',
    ].filter(Boolean),
  };
}

export function getServiceConversionSummaryForDisplay(
  service = {},
  {
    bookingRequests = [],
    bookings = [],
    lessons = [],
  } = {}
) {
  const normalizedService = safeNormalizeService(service);
  const relatedRequests = (bookingRequests || []).filter((request) => (request.serviceId || '') === normalizedService.serviceId);
  const relatedBookings = (bookings || []).filter((booking) => (booking.serviceId || '') === normalizedService.serviceId);
  const relatedLessons = (lessons || []).filter((lesson) => (lesson.serviceId || '') === normalizedService.serviceId);
  return {
    requestCount: relatedRequests.length,
    bookingCount: relatedBookings.length,
    lessonCount: relatedLessons.length,
    conversionHint: relatedRequests.length > 0
      ? `${relatedBookings.length} of ${relatedRequests.length} requests progressed to a booking.`
      : `${relatedBookings.length} confirmed booking${relatedBookings.length === 1 ? '' : 's'} so far.`,
  };
}

export function buildServicePublicPage(
  service = {},
  {
    memberships = [],
    servicePackages = [],
    packageUsages = [],
    bookingRequests = [],
    bookings = [],
    lessons = [],
  } = {}
) {
  const normalizedService = safeNormalizeService(service);
  const pricingSummary = getServicePricingSummary(normalizedService, { memberships });
  const recommendedPackage = getRecommendedPackageForService(normalizedService, {
    memberships,
    servicePackages,
  });
  const popularPackage = getMostPopularPackageForService(normalizedService, {
    servicePackages,
    packageUsages,
  });
  const conversionSummary = getServiceConversionSummaryForDisplay(normalizedService, {
    bookingRequests,
    bookings,
    lessons,
  });
  return {
    ...normalizedService,
    pricingSummary,
    recommendedPackage,
    popularPackage,
    conversionSummary,
    whoItsFor: buildServiceAudience(normalizedService),
    whatYoullLearn: buildServiceLearningOutcomes(normalizedService),
    ctaLabel: 'Book single lesson',
    packageCtaLabel: recommendedPackage ? 'Buy package' : 'Explore packages',
  };
}

export function getLessonDetailLink(lesson = {}) {
  const lessonId = lesson.lessonId || lesson.id || '';
  return lessonId ? `/lessons/${lessonId}` : '/lessons';
}

export function getVideoFeedbackLink(video = {}) {
  const videoId = video.id || video.videoId || '';
  return videoId ? `/reviews/${videoId}?section=feedback` : '/me/progress';
}

export function getDrillDetailLink(drill = {}) {
  const drillId = drill.id || drill.drillId || drill.skillId || '';
  return drillId ? `/my-skills?drill=${encodeURIComponent(drillId)}&section=drills` : '/my-skills';
}

export function getRebookLink(
  {
    nextService = null,
    recommendedInstructor = null,
  } = {}
) {
  const params = new URLSearchParams();
  if (nextService?.serviceId) params.set('serviceId', nextService.serviceId);
  if (recommendedInstructor?.userId) params.set('instructorId', recommendedInstructor.userId);
  params.set('rebook', '1');
  const query = params.toString();
  return query ? `/my-bookings?${query}` : '/my-bookings';
}

export function getPackageSelectionLink(
  servicePackage = {},
  {
    service = null,
    instructor = null,
    organizationId = '',
  } = {}
) {
  const params = new URLSearchParams();
  const packageId = servicePackage.servicePackageId || servicePackage.packageId || '';
  if (packageId) params.set('packageId', packageId);
  if (service?.serviceId) params.set('serviceId', service.serviceId);
  if (instructor?.userId) params.set('instructorId', instructor.userId);
  if (organizationId) params.set('organizationId', organizationId);
  const query = params.toString();
  return query ? `/dashboard/packages?${query}` : '/dashboard/packages';
}

export function getAnchoredReviewLink(video = {}, section = 'feedback') {
  const videoId = video.id || video.videoId || '';
  const params = new URLSearchParams();
  if (section) params.set('section', section);
  const query = params.toString();
  return videoId ? `/reviews/${videoId}${query ? `?${query}` : ''}` : '/me/progress';
}

export function getStudentLessonHistory(lessonInstances = []) {
  return [...(lessonInstances || [])].sort((left, right) => toTimestampValue(right.updatedAt || right.lessonDate || right.createdAt) - toTimestampValue(left.updatedAt || left.lessonDate || left.createdAt));
}

export function getStudentVideoHistory(videos = []) {
  return [...(videos || [])].sort((left, right) => toTimestampValue(right.createdAt || right.reviewedAt) - toTimestampValue(left.createdAt || left.reviewedAt));
}

export function getStudentFeedbackSummary(videos = [], feedbackMap = {}) {
  const reviewedVideos = (videos || []).filter((video) => video.status === 'reviewed');
  const feedbackCount = reviewedVideos.reduce((total, video) => total + ((feedbackMap[video.id] || []).length > 0 ? 1 : 0), 0);
  const recentReviewedVideo = reviewedVideos.sort((left, right) => toTimestampValue(right.reviewedAt || right.createdAt) - toTimestampValue(left.reviewedAt || left.createdAt))[0] || null;
  return {
    reviewedVideoCount: reviewedVideos.length,
    feedbackReadyCount: feedbackCount,
    latestReviewedVideoName: recentReviewedVideo?.videoName || recentReviewedVideo?.title || '',
  };
}

export function getStudentDrillAssignments(progressSummary = {}) {
  return (progressSummary.topDrills || []).map((drill) => ({
    title: drill.label || drill.title || 'Assigned drill',
    reason: drill.description || drill.reason || '',
  }));
}

export function getNextRecommendedService(clientProfile = {}, options = {}) {
  return suggestNextServiceForClient(clientProfile, options);
}

export function getRecommendedInstructorForNextLesson(
  {
    bookings = [],
    instructors = [],
    nextService = null,
  } = {}
) {
  const relevantBookings = (bookings || [])
    .map((booking) => normalizeBooking(booking))
    .sort((left, right) => toTimestampValue(right.startAt || right.updatedAt) - toTimestampValue(left.startAt || left.updatedAt));
  const recentInstructorId = relevantBookings[0]?.instructorId || nextService?.instructorId || '';
  return (instructors || []).find((instructor) => (instructor.userId || instructor.id) === recentInstructorId) || null;
}

export function buildRebookRecommendation(
  clientProfile = {},
  {
    bookings = [],
    services = [],
    instructors = [],
    memberships = [],
    servicePackages = [],
    feedbackHistory = [],
    now = new Date(),
  } = {}
) {
  const recommendations = buildClientNextStepRecommendations(clientProfile, {
    bookings,
    services,
    memberships,
    servicePackages,
    feedbackHistory,
    now,
  });
  return {
    ...recommendations,
    nextService: recommendations.nextService || getNextRecommendedService(clientProfile, { bookings, services }),
    recommendedInstructor: getRecommendedInstructorForNextLesson({
      bookings,
      instructors,
      nextService: recommendations.nextService,
    }),
    continueProgressLabel: recommendations.rebookTiming?.timing === 'now'
      ? 'Continue your progress now'
      : 'Continue your progress',
  };
}

export function buildContinueProgressCTA(clientProfile = {}, options = {}) {
  const recommendation = buildRebookRecommendation(clientProfile, options);
  return {
    label: recommendation.continueProgressLabel || 'Continue your progress',
    href: getRebookLink(recommendation),
    helperText: recommendation.rebookTiming?.message || 'Book your next session when you are ready.',
  };
}

export function buildPostFeedbackRebookPrompt(
  clientProfile = {},
  {
    videos = [],
    feedbackMap = {},
    services = [],
    bookings = [],
    instructors = [],
    memberships = [],
    servicePackages = [],
  } = {}
) {
  const latestReviewedVideo = getStudentVideoHistory(videos).find((video) => video.status === 'reviewed') || null;
  const latestFeedback = latestReviewedVideo ? (feedbackMap[latestReviewedVideo.id] || [])[0] : null;
  const recommendation = buildRebookRecommendation(clientProfile, {
    bookings,
    services,
    instructors,
    memberships,
    servicePackages,
    feedbackHistory: Object.values(feedbackMap || {}).flat(),
  });

  return {
    title: 'After your feedback, keep the momentum going',
    message: latestFeedback?.content
      ? `Your latest feedback highlighted: ${latestFeedback.content}`
      : 'Your reviewed session has fresh feedback ready to turn into the next lesson.',
    nextService: recommendation.nextService,
    recommendedInstructor: recommendation.recommendedInstructor,
    recommendedPackage: recommendPackageAfterVideo(latestReviewedVideo || {}, {
      clientProfile,
      memberships,
      servicePackages,
      bookings,
    }),
    href: getRebookLink(recommendation),
  };
}

export function buildPostLessonRebookPrompt(
  clientProfile = {},
  {
    lessonInstances = [],
    services = [],
    bookings = [],
    instructors = [],
    memberships = [],
    servicePackages = [],
  } = {}
) {
  const latestLesson = getStudentLessonHistory(lessonInstances)[0] || null;
  const recommendation = buildRebookRecommendation(clientProfile, {
    bookings,
    services,
    instructors,
    memberships,
    servicePackages,
  });

  return {
    title: 'Turn your last lesson into the next one',
    message: latestLesson
      ? `Your latest completed lesson can roll straight into ${recommendation.nextService?.name || 'your next session'}.`
      : 'A next lesson suggestion will appear once you have completed lessons.',
    latestLesson,
    nextService: recommendation.nextService,
    recommendedInstructor: recommendation.recommendedInstructor,
    recommendedPackage: recommendPackageAfterLesson(latestLesson || {}, {
      clientProfile,
      memberships,
      servicePackages,
      bookings,
    }),
    href: getRebookLink(recommendation),
  };
}

export function buildNextBestBookingSuggestion(clientProfile = {}, options = {}) {
  const recommendation = buildRebookRecommendation(clientProfile, options);
  return {
    title: recommendation.nextService?.name || 'Continue your coaching progress',
    recommendedInstructor: recommendation.recommendedInstructor,
    timing: recommendation.rebookTiming,
    href: getRebookLink(recommendation),
    packageRecommendation: recommendPackageForClient(clientProfile, {
      memberships: options.memberships,
      servicePackages: options.servicePackages,
      bookings: options.bookings,
    }),
  };
}

export function getMostPopularPackageForDisplay(service = {}, options = {}) {
  const pkg = getMostPopularPackageForService(service, options);
  if (!pkg) return null;
  return {
    ...pkg,
    badge: 'Most popular package',
  };
}

export function getBestNextLessonForDisplay(clientProfile = {}, options = {}) {
  const recommendation = buildRebookRecommendation(clientProfile, options);
  if (!recommendation.nextService) return null;
  return {
    ...recommendation.nextService,
    helperText: recommendation.rebookTiming?.message || 'A practical next session based on your recent coaching data.',
  };
}

export function getRecommendedNextServiceForDisplay(clientProfile = {}, options = {}) {
  return getBestNextLessonForDisplay(clientProfile, options);
}

export function getStudentMilestones(
  {
    lessonHistory = [],
    videoHistory = [],
    feedbackSummary = {},
    drillAssignments = [],
  } = {}
) {
  const milestones = [];
  const completedLessons = lessonHistory.filter((lesson) => lesson.status === 'completed').length;
  const uploadedVideos = videoHistory.length;
  const feedbackReady = feedbackSummary.feedbackReadyCount || 0;

  if (completedLessons >= 1) milestones.push({ key: 'lessons_started', label: `${completedLessons} lesson${completedLessons === 1 ? '' : 's'} completed` });
  if (completedLessons >= 5) milestones.push({ key: 'lesson_streak', label: '5-lesson progress milestone reached' });
  if (uploadedVideos >= 1) milestones.push({ key: 'videos_uploaded', label: `${uploadedVideos} video${uploadedVideos === 1 ? '' : 's'} uploaded` });
  if (feedbackReady >= 1) milestones.push({ key: 'feedback_ready', label: `${feedbackReady} feedback cycle${feedbackReady === 1 ? '' : 's'} completed` });
  if (drillAssignments.length >= 1) milestones.push({ key: 'drills_assigned', label: `${drillAssignments.length} active drill${drillAssignments.length === 1 ? '' : 's'} assigned` });

  return milestones;
}

export function getStudentAchievements(input = {}) {
  return getStudentMilestones(input).slice(0, 3);
}

export function getStudentLevelEstimate(progressSummary = {}) {
  return progressSummary.currentLevelSummary || progressSummary.currentLevel || progressSummary.currentSkill?.name || 'Building foundations';
}

export function getStudentProgressHighlights(
  {
    progressSummary = {},
    lessonHistory = [],
    videoHistory = [],
    feedbackSummary = {},
    drillAssignments = [],
  } = {}
) {
  return [
    progressSummary.currentSkill?.name ? `Current focus: ${progressSummary.currentSkill.name}` : '',
    lessonHistory[0]?.title ? `Latest lesson: ${lessonHistory[0].title}` : '',
    videoHistory[0]?.videoName ? `Latest review: ${videoHistory[0].videoName}` : '',
    feedbackSummary.latestReviewedVideoName ? `Feedback ready on ${feedbackSummary.latestReviewedVideoName}` : '',
    drillAssignments[0]?.title ? `Next drill: ${drillAssignments[0].title}` : '',
  ].filter(Boolean);
}

export function buildProgressMomentSummary(
  {
    clientProfile = {},
    progressSummary = {},
    milestones = [],
    achievements = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const topMoment = achievements[0] || milestones[0] || null;
  return {
    title: topMoment?.label || `${normalizedProfile.displayName || 'Student'} is building momentum`,
    summary: progressSummary.currentSkill?.name
      ? `Current focus is ${progressSummary.currentSkill.name}.`
      : 'A fresh progress moment will appear after more lessons or feedback cycles.',
    shareLabel: 'Share progress',
  };
}

export function buildStudentProgressDashboard(
  {
    clientProfile = {},
    videos = [],
    feedbackMap = {},
    lessonInstances = [],
    bookings = [],
    memberships = [],
    services = [],
    servicePackages = [],
  } = {}
) {
  const normalizedProfile = normalizeClientProfile(clientProfile);
  const progressSummary = buildStudentProgressSummary(videos, { feedbackMap, lessonInstances });
  const feedbackSummary = getStudentFeedbackSummary(videos, feedbackMap);
  const lessonHistory = getStudentLessonHistory(lessonInstances);
  const videoHistory = getStudentVideoHistory(videos);
  const drillAssignments = getStudentDrillAssignments(progressSummary);
  const nextStep = buildRebookRecommendation(normalizedProfile, {
    bookings,
    services,
    memberships,
    servicePackages,
    feedbackHistory: Object.values(feedbackMap || {}).flat(),
  });
  const packageRecommendation = recommendPackageForClient(normalizedProfile, {
    memberships,
    servicePackages,
    bookings,
  });
  const packageSelection = buildPackageSelectionView(nextStep.nextService || services[0] || {}, {
    servicePackages,
    memberships,
    clientProfile: normalizedProfile,
  });
  const postFeedbackPrompt = buildPostFeedbackRebookPrompt(normalizedProfile, {
    videos,
    feedbackMap,
    services,
    bookings,
    memberships,
    servicePackages,
  });
  const postLessonPrompt = buildPostLessonRebookPrompt(normalizedProfile, {
    lessonInstances,
    services,
    bookings,
    memberships,
    servicePackages,
  });
  const continueProgressCta = buildContinueProgressCTA(normalizedProfile, {
    bookings,
    services,
    memberships,
    servicePackages,
    feedbackHistory: Object.values(feedbackMap || {}).flat(),
  });
  const nextBookingSuggestion = buildNextBestBookingSuggestion(normalizedProfile, {
    bookings,
    services,
    memberships,
    servicePackages,
    feedbackHistory: Object.values(feedbackMap || {}).flat(),
  });
  const milestones = getStudentMilestones({
    lessonHistory,
    videoHistory,
    feedbackSummary,
    drillAssignments,
  });

  return {
    progressSummary,
    lessonHistory,
    videoHistory,
    feedbackSummary,
    drillAssignments,
    nextStep,
    packageRecommendation,
    packageSelection,
    activeMemberships: (memberships || []).map((membership) => normalizeCustomerMembership(membership)).filter((membership) => membership.userId === normalizedProfile.userId || !normalizedProfile.userId),
    postFeedbackPrompt,
    postLessonPrompt,
    continueProgressCta,
    nextBookingSuggestion,
    milestones,
    achievements: getStudentAchievements({
      lessonHistory,
      videoHistory,
      feedbackSummary,
      drillAssignments,
    }),
    levelEstimate: getStudentLevelEstimate(progressSummary),
    progressHighlights: getStudentProgressHighlights({
      progressSummary,
      lessonHistory,
      videoHistory,
      feedbackSummary,
      drillAssignments,
    }),
  };
}
